import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import CategoryCard from '@/components/feature/CategoryCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import NotFound from '@/pages/NotFound';
import { categories, getArticlesByCategory, getCategoryBySlug } from '@/mocks/data';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const [activeSlug, setActiveSlug] = useState(slug || '');

  const category = getCategoryBySlug(activeSlug);
  const articles = getArticlesByCategory(activeSlug);

  if (!category) {
    return <NotFound />;
  }

  const breadcrumbItems = [
    { label: category.name },
  ];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://boldniche.github.io/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: category.name,
      },
    ],
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 反差设计研究所`,
    description: category.description,
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
      />

      <Navbar />

      <main className="pt-20">
        {/* Category Header */}
        <section className="relative w-full h-[280px] md:h-[350px] flex items-center justify-center overflow-hidden">
          <img
            src={category.image}
            alt={category.name}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/60"></div>
          <div className="relative z-10 text-center px-4 md:px-6">
            <span className="inline-flex items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-white/15 backdrop-blur-sm mb-4">
              <i className={`${category.icon} text-white text-2xl`}></i>
            </span>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-white">
              {category.name}
            </h1>
            <p className="text-white/70 text-sm md:text-base mt-3 max-w-lg mx-auto">
              {category.description}
            </p>
          </div>
        </section>

        {/* Category Tabs + Content */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            {/* Category Switcher */}
            <div className="flex gap-2 overflow-x-auto flex-wrap pb-2 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveSlug(cat.slug)}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 no-underline cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* Articles List */}
            {articles.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
                {articles.map((article) => (
                  <ArticleCard key={article.slug} article={article} variant="horizontal" />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-foreground-500">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}